import { useState, useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { Markdown } from 'tiptap-markdown';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Quote,
  Undo,
  Redo,
  Code2,
  FileText,
  Link as LinkIcon,
  Code,
  Minus,
  ListChecks,
  ImageIcon,
  ChevronDown,
  Table2,
} from 'lucide-react';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: string;
}

export default function RichTextEditor({
  content,
  onChange,
  placeholder = 'Write something...',
  disabled = false,
  minHeight = '120px',
}: RichTextEditorProps) {
  const [markdownMode, setMarkdownMode] = useState(false);
  const [markdownText, setMarkdownText] = useState('');

  const isMarkdown = (text: string) => {
    if (!text || text.startsWith('<')) return false;
    return /(?:^|\n)#{1,6}\s|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|^\s*[-*+]\s|^\s*\d+\.\s|^>\s|`[^`]+`|\[.+\]\(.+\)|^---$/m.test(text);
  };

  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const headingMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headingMenuRef.current && !headingMenuRef.current.contains(e.target as Node)) {
        setShowHeadingMenu(false);
      }
    };
    if (showHeadingMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHeadingMenu]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Placeholder.configure({ placeholder }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-brand-600 underline' },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'not-prose border-collapse w-full' },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: false,
      }),
    ],
    content: isMarkdown(content) ? content : content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const toggleMarkdownMode = useCallback(() => {
    if (!editor) return;

    if (!markdownMode) {
      const md = (editor.storage as any).markdown.getMarkdown();
      setMarkdownText(md);
    } else {
      editor.commands.setContent(markdownText);
      onChange(editor.getHTML());
    }
    setMarkdownMode(!markdownMode);
  }, [editor, markdownMode, markdownText, onChange]);

  const handleMarkdownChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMarkdownText(val);
    if (editor) {
      editor.commands.setContent(val);
      onChange(editor.getHTML());
    }
  }, [editor, onChange]);

  if (!editor) return null;

  const ToolbarButton = ({
    onClick,
    isActive = false,
    children,
    title,
  }: {
    onClick: () => void;
    isActive?: boolean;
    children: React.ReactNode;
    title: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );

  const getActiveHeadingLevel = (): number | null => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return i;
    }
    return null;
  };

  const headingLabel = getActiveHeadingLevel() ? `H${getActiveHeadingLevel()}` : 'Text';

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (!url) return;
    editor.chain().focus().setLink({ href: url }).run();
  };

  const insertImage = () => {
    const url = window.prompt('Enter image URL:');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 px-2 py-1.5 dark:border-gray-700">
        {!markdownMode && (
          <>
            {/* Heading dropdown */}
            <div className="relative" ref={headingMenuRef}>
              <button
                type="button"
                onClick={() => setShowHeadingMenu(!showHeadingMenu)}
                title="Heading level"
                className={`flex items-center gap-0.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                  getActiveHeadingLevel()
                    ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <Heading1 className="h-4 w-4" />
                <span>{headingLabel}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {showHeadingMenu && (
                <div className="absolute top-full left-0 mt-1 z-50 w-36 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 py-1">
                  <button
                    type="button"
                    onClick={() => { editor.chain().focus().setParagraph().run(); setShowHeadingMenu(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                      !getActiveHeadingLevel()
                        ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    Normal text
                  </button>
                  {([1, 2, 3, 4, 5, 6] as const).map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => { editor.chain().focus().toggleHeading({ level }).run(); setShowHeadingMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 transition-colors ${
                        editor.isActive('heading', { level })
                          ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      style={{ fontSize: `${Math.max(0.75, 1.1 - level * 0.08)}rem` }}
                    >
                      Heading {level}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              title="Underline"
            >
              <UnderlineIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              isActive={editor.isActive('strike')}
              title="Strikethrough"
            >
              <Strikethrough className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCode().run()}
              isActive={editor.isActive('code')}
              title="Inline Code"
            >
              <Code className="h-4 w-4" />
            </ToolbarButton>

            <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
              title="Ordered List"
            >
              <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleTaskList().run()}
              isActive={editor.isActive('taskList')}
              title="Task List"
            >
              <ListChecks className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive('blockquote')}
              title="Quote"
            >
              <Quote className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              isActive={editor.isActive('codeBlock')}
              title="Code Block"
            >
              <Code2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              title="Horizontal Rule"
            >
              <Minus className="h-4 w-4" />
            </ToolbarButton>

            <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />

            <ToolbarButton
              onClick={insertLink}
              isActive={editor.isActive('link')}
              title="Insert Link"
            >
              <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={insertImage}
              title="Insert Image"
            >
              <ImageIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
              title="Insert Table"
            >
              <Table2 className="h-4 w-4" />
            </ToolbarButton>

            <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />

            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              title="Undo"
            >
              <Undo className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              title="Redo"
            >
              <Redo className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}

        {markdownMode && (
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1 select-none">
            Markdown
          </span>
        )}

        <div className="flex-1" />

        <ToolbarButton
          onClick={toggleMarkdownMode}
          isActive={markdownMode}
          title={markdownMode ? 'Switch to WYSIWYG' : 'Switch to Markdown'}
        >
          {markdownMode ? <FileText className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        </ToolbarButton>
      </div>

      {markdownMode ? (
        <textarea
          value={markdownText}
          onChange={handleMarkdownChange}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full resize-none bg-transparent px-4 py-3 font-mono text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
          style={{ minHeight }}
        />
      ) : (
        <EditorContent
          editor={editor}
          className="prose prose-sm dark:prose-invert max-w-none px-4 py-3 focus-within:outline-none [&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0 [&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2 [&_ul[data-type=taskList]_li_label]:mt-0.5 [&_table]:border-collapse [&_table]:w-full [&_table]:my-3 [&_th]:border [&_th]:border-gray-300 [&_th]:dark:border-gray-600 [&_th]:bg-gray-50 [&_th]:dark:bg-gray-700/50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_th]:text-gray-700 [&_th]:dark:text-gray-300 [&_td]:border [&_td]:border-gray-300 [&_td]:dark:border-gray-600 [&_td]:px-3 [&_td]:py-2 [&_td]:text-sm [&_td]:text-gray-700 [&_td]:dark:text-gray-300"
          style={{ minHeight }}
        />
      )}
    </div>
  );
}
